import { useMemo, useState } from 'react';
import {
  STAT_ROW_ID,
  STAT_ROW_OWNER,
  type StatColumn,
  type StatFilter,
  type StatRow,
  type StatSort,
  type StatTable as StatTableData,
  type StatValue,
} from '@gl/shared';

import { OwnTeamMark, ownRowClass, useOwnSleeperId } from './ownTeam.js';

/**
 * The one renderer for every statistic.
 *
 * Statistics arrive from the API already shaped as columns and rows, so nothing
 * here knows what it is drawing — adding a statistic on the server needs no
 * change in this file.
 */

function formatValue(value: StatValue, format: StatColumn['format']): string {
  // A missing value is a real state (a team with no losses has no average), and
  // reads better as a dash than as a zero it did not earn.
  if (value === null) return '—';
  if (typeof value === 'string') return value;

  switch (format) {
    case 'integer':
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    case 'decimal1':
      return value.toFixed(1);
    case 'decimal2':
      return value.toFixed(2);
    case 'signed2':
      return `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
    case 'percent1':
      return `${value.toFixed(1)}%`;
    case 'rank':
      return `#${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    default:
      return String(value);
  }
}

/** Numbers compare numerically; anything else compares as human-readable text. */
function compareValues(a: StatValue, b: StatValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en', { numeric: true });
}

/**
 * Which way to sort when a column is first clicked. Numbers lead with their
 * biggest values, names with A.
 */
function initialDirection(column: StatColumn, rows: StatTableData['rows']): StatSort['direction'] {
  if (column.format && column.format !== 'text') return 'desc';
  const sample = rows.find((row) => row[column.key] !== null && row[column.key] !== undefined);
  return typeof sample?.[column.key] === 'number' ? 'desc' : 'asc';
}

/**
 * Rows are re-ordered by sorting, so they need an identity that survives it.
 * Calculators supply one; position is the fallback for any that has not been
 * updated yet, and is only wrong while a sort animates.
 */
function rowKey(row: StatRow, index: number): string {
  const id = row[STAT_ROW_ID];
  return typeof id === 'string' || typeof id === 'number' ? String(id) : `row-${index}`;
}

/**
 * Whether a row is the reader's own franchise. Rows carry the manager's Sleeper
 * user id rather than a name, so nothing here depends on what anyone calls
 * themselves this season.
 */
function isOwnRow(row: StatRow, mine: string | null): boolean {
  return mine !== null && row[STAT_ROW_OWNER] === mine;
}

/** Which column the "You" badge belongs beside: the team, or failing that the manager. */
function ownMarkColumn(columns: StatColumn[]): string | null {
  return columns.find((column) => column.key === 'team' || column.key === 'manager')?.key ?? null;
}

/** The value a filter matches on, as a string, or null when the row has none. */
function filterValue(row: StatRow, key: string): string | null {
  const value = row[key];
  return value === null || value === undefined ? null : String(value);
}

/**
 * The toggles a filter offers: the order the calculator asked for first, then
 * anything else in row order, so a value can never go missing from the list.
 */
function filterOptions(filter: StatFilter, rows: StatRow[]): string[] {
  const present = new Set<string>();
  for (const row of rows) {
    const value = filterValue(row, filter.key);
    if (value !== null) present.add(value);
  }

  const ordered = (filter.options ?? []).filter((option) => present.has(option));
  return [...ordered, ...[...present].filter((value) => !ordered.includes(value))];
}

/** RFC 4180: quote anything that could be misread, and double any inner quote. */
function csvCell(value: StatValue): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/**
 * The visible table as CSV — the columns as shown, the rows as filtered and
 * sorted, and the raw values rather than the formatted ones so a spreadsheet
 * can still add them up.
 */
function toCsv(columns: StatColumn[], rows: StatRow[]): string {
  const lines = [columns.map((column) => csvCell(column.label)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((column) => csvCell(row[column.key] ?? null)).join(','));
  }
  // A trailing newline keeps the last row intact for tools that read by line.
  return `${lines.join('\r\n')}\r\n`;
}

/** Something safe to save under, built from whatever names the caller gave. */
function csvFilename(parts: Array<string | undefined>): string {
  const slug = parts
    .filter((part): part is string => Boolean(part))
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug || 'stats'}.csv`;
}

function download(filename: string, contents: string): void {
  // A BOM is what makes Excel read the file as UTF-8 rather than as Latin-1,
  // which is otherwise where team names with an accent go wrong.
  const blob = new Blob([`\uFEFF${contents}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function SortIndicator({ state }: { state: 'asc' | 'desc' | null }) {
  return (
    <span aria-hidden className={state ? 'text-brass-400' : 'text-field-600'}>
      {state === 'asc' ? '▲' : state === 'desc' ? '▼' : '↕'}
    </span>
  );
}

export function StatTable({
  table,
  title,
  season,
}: {
  table: StatTableData;
  /** Names the downloaded file; the statistic's own title. */
  title?: string;
  season?: string;
}) {
  // The API returns rows already in `defaultSort` order. Seeding the state with
  // it means the first paint matches the server exactly (the sort is stable, so
  // the server's tie-breaking survives) while still labelling the sorted column.
  const [sort, setSort] = useState<StatSort | null>(table.defaultSort ?? null);

  // Held as what is switched *on*. Nothing selected means no filter at all, so
  // every row shows — including values that only exist in some seasons, which
  // can therefore never go missing from a table.
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  const mine = useOwnSleeperId();
  const markColumn = ownMarkColumn(table.columns);
  const leadKey = sort?.key ?? table.defaultSort?.key ?? null;

  const filters = table.filters ?? [];

  const filtered = useMemo(() => {
    if (filters.length === 0) return table.rows;

    return table.rows.filter((row) =>
      filters.every((filter) => {
        const on = selected[filter.key];
        if (!on || on.length === 0) return true;
        const value = filterValue(row, filter.key);
        return value !== null && on.includes(value);
      }),
    );
  }, [table.rows, filters, selected]);

  const rows = useMemo(() => {
    if (!sort) return filtered;
    const direction = sort.direction === 'asc' ? 1 : -1;

    return [...filtered].sort((left, right) => {
      const a = left[sort.key] ?? null;
      const b = right[sort.key] ?? null;
      // Blanks sink to the bottom either way round; they are absences, not
      // extremes, and shuffling them to the top buries the real leaders.
      if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
      return compareValues(a, b) * direction;
    });
  }, [filtered, sort]);

  const toggle = (column: StatColumn) => {
    setSort((current) =>
      current?.key === column.key
        ? { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column.key, direction: initialDirection(column, table.rows) },
    );
  };

  const toggleValue = (filter: StatFilter, value: string) => {
    setSelected((current) => {
      const on = current[filter.key] ?? [];
      return {
        ...current,
        [filter.key]: on.includes(value) ? on.filter((each) => each !== value) : [...on, value],
      };
    });
  };

  if (table.rows.length === 0) {
    return (
      <p className="px-1 py-6 text-sm text-field-400">Nothing qualified for this statistic yet.</p>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {filters.map((filter) => {
            const options = filterOptions(filter, table.rows);
            const on = selected[filter.key] ?? [];
            if (options.length < 2) return null;

            return (
              <div key={filter.key} className="flex flex-wrap items-center gap-1.5">
                <span className="mr-0.5 font-display text-xs font-semibold uppercase tracking-bug text-field-400">
                  {filter.label}
                </span>
                {/*
                  Nothing selected is the unfiltered state, so "All" is lit
                  rather than being an action that only appears once something
                  is off — pressing it just clears the selection.
                */}
                <button
                  type="button"
                  aria-pressed={on.length === 0}
                  onClick={() => setSelected((current) => ({ ...current, [filter.key]: [] }))}
                  className={`border px-2.5 py-0.5 font-display text-xs font-semibold uppercase
                    tracking-bug transition-colors ${
                      on.length === 0
                        ? 'border-brass-400 bg-brass-400 text-field-950'
                        : 'border-field-700 bg-field-900 text-field-400 hover:text-field-100'
                    }`}
                >
                  All
                </button>
                {options.map((option) => {
                  const active = on.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleValue(filter, option)}
                      className={`border px-2.5 py-0.5 font-display text-xs font-semibold uppercase
                        tracking-bug transition-colors ${
                          active
                            ? 'border-brass-400 bg-brass-400 text-field-950'
                            : 'border-field-700 bg-field-900 text-field-500 hover:text-field-100'
                        }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="btn-secondary ml-auto px-2.5 py-1 text-[0.6875rem]"
          onClick={() => download(csvFilename([title, season]), toCsv(table.columns, rows))}
        >
          ↓ CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="px-1 py-6 text-sm text-field-400">
          Nothing matches the filters above — press All to clear them.
        </p>
      ) : (
        <div className="table-scroll -mx-4 sm:-mx-5">
          <table className="bx-table">
            <thead>
              <tr>
                {table.columns.map((column) => {
                  const active = sort?.key === column.key ? sort.direction : null;
                  const right = column.align === 'right';

                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={active ? (active === 'asc' ? 'ascending' : 'descending') : 'none'}
                      // Top-aligned so every column label sits on the same line
                      // even when a neighbour carries a two-line help sentence.
                      className={`p-0 align-bottom ${right ? 'text-right' : 'text-left'}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(column)}
                        // A help sentence has no minimum width of its own, so a
                        // narrow viewport wrings it into a tower that drags every
                        // other header down with it. The floor keeps it readable
                        // and lets the table scroll instead.
                        className={`w-full max-w-[16rem] px-3 py-1.5 font-display text-xs
                        font-semibold uppercase tracking-bug transition-colors hover:bg-field-700
                        ${right ? 'text-right' : 'text-left'}
                        ${active ? 'text-brass-300' : 'text-field-100'}
                        ${column.help ? 'min-w-[10rem]' : ''}`}
                      >
                        <span
                          className={`flex items-baseline gap-1.5 ${right ? 'justify-end' : ''}`}
                        >
                          {column.label}
                          <SortIndicator state={active} />
                        </span>
                        {column.help ? (
                          <span
                            className="mt-0.5 block whitespace-normal font-sans text-[0.6875rem]
                          font-normal normal-case tracking-normal text-field-500"
                          >
                            {column.help}
                          </span>
                        ) : null}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const own = isOwnRow(row, mine);
                return (
                  <tr key={rowKey(row, index)} className={ownRowClass(own)}>
                    {table.columns.map((column) => {
                      const right = column.align === 'right';
                      // The column the table is sorted on is the story it is
                      // telling, so that number is set at display size and every
                      // other one stays out of its way.
                      const lead = right && column.key === leadKey;

                      return (
                        <td
                          key={column.key}
                          className={`whitespace-nowrap px-3 ${lead ? 'py-1' : 'py-1.5'} ${
                            right
                              ? `text-right tabular-nums ${lead ? 'lead-figure' : 'text-field-100'}`
                              : 'text-left text-field-200'
                          }`}
                        >
                          {formatValue(row[column.key] ?? null, column.format)}
                          {own && column.key === markColumn ? <OwnTeamMark /> : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {table.note ? (
        <p className="mt-3 border-l-2 border-field-700 pl-2 text-xs leading-relaxed text-field-500">
          {table.note}
        </p>
      ) : null}
    </div>
  );
}
